import React, { useEffect, useState, useMemo } from "react";
import { formatLocalDateTime } from "./utils/dates";
import axios from "axios";

const API_BASE =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production'
    ? "/api"
    : "http://localhost:8000");


const axiosInstance = axios.create({ baseURL: API_BASE, withCredentials: true, headers: { 'Content-Type': 'application/json' } });

function formatRemaining(seconds) {
  if (!seconds || seconds <= 0) return "";
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  const parts = [];
  if (hrs) parts.push(`${hrs} hour${hrs !== 1 ? 's' : ''}`);
  if (mins) parts.push(`${mins} minute${mins !== 1 ? 's' : ''}`);
  if (!hrs && !mins) parts.push(`${secs} second${secs !== 1 ? 's' : ''}`);
  return parts.join(" ");
}

export default function SteamStatusUpdate() {
  const [status, setStatus] = useState({ can_refresh: true, cooldown_remaining_seconds: 0, last_manual_refresh: null });
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  async function fetchStatus() {
    try {
      const res = await axiosInstance.get('/manual-refresh/status');
      setStatus(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load status');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchStatus();
    // optional: live countdown
    const interval = setInterval(() => {
      setStatus(prev => ({
        ...prev,
        cooldown_remaining_seconds: prev.can_refresh ? 0 : Math.max(0, (prev.cooldown_remaining_seconds || 0) - 1)
      }));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  async function doRefresh() {
    setUpdating(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await axiosInstance.post('/manual-refresh');
      setSuccess(`Updated ${res.data.updated_profiles.length} profiles`);
      setStatus({ can_refresh: false, cooldown_remaining_seconds: 2 * 3600, last_manual_refresh: res.data.last_manual_refresh });
    } catch (e) {
      const rem = e.response?.data?.cooldown_remaining_seconds;
      setError(e.response?.data?.error || 'Refresh failed');
      if (typeof rem === 'number') {
        setStatus(prev => ({ ...prev, can_refresh: false, cooldown_remaining_seconds: rem }));
      }
    } finally {
      setUpdating(false);
    }
  }

  const lastUpdatedText = useMemo(() => {
    if (!status.last_manual_refresh) return 'Never';
    return formatLocalDateTime(status.last_manual_refresh);
  }, [status.last_manual_refresh]);

  if (loading) return <div style={{ textAlign: 'center', marginTop: '40px' }}>Loading status...</div>;

  const canClick = status.can_refresh && !updating;
  const remainingText = !status.can_refresh ? `${formatRemaining(status.cooldown_remaining_seconds)} until a new update` : '';

  return (
    <div style={{ maxWidth: '700px', margin: '30px auto', fontFamily: 'Arial, sans-serif' }}>
      <h2 style={{ marginBottom: '10px' }}>Steam status update</h2>
      <p style={{ color: '#666', marginTop: 0 }}>Update bans for all accounts you track.</p>

      {error && (
        <div style={{ background: '#f8d7da', color: '#721c24', padding: '10px', borderRadius: '4px', marginBottom: '10px' }}>{error}</div>
      )}
      {success && (
        <div style={{ background: '#d4edda', color: '#155724', padding: '10px', borderRadius: '4px', marginBottom: '10px' }}>{success}</div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
        <button
          onClick={doRefresh}
          disabled={!canClick}
          style={{
            padding: '10px 20px',
            backgroundColor: canClick ? '#007bff' : '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: canClick ? 'pointer' : 'not-allowed'
          }}
        >
          {updating ? 'Updating...' : 'Update now'}
        </button>
        {!status.can_refresh && (
          <span style={{ color: '#ff9800', fontWeight: 'bold' }}>{remainingText}</span>
        )}
      </div>

      <div style={{ marginTop: '15px', fontSize: '14px', color: '#333' }}>
        <strong>Last Updated:</strong> {lastUpdatedText}
      </div>

      <button 
        onClick={() => window.history.back()} 
        style={{ 
          marginTop: '20px',
          padding: '8px 16px',
          backgroundColor: '#6c757d',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer'
        }}
      >
        Go Back
      </button>
    </div>
  );
}


